import { call, put, race, select, take } from 'redux-saga/effects';
import { delay } from 'redux-saga';
import { transactionPayloadSelector } from 'ctm-selectors';
import { apiAction, mapStateToArgs } from 'ctm-util-functions';
import pageSchema from '../../schemas/page_schema.json';

// eslint-disable-next-line valid-jsdoc
/**
 * Do not proceed if journey has no associated transaction
 */
const postPayloadIfTransaction = function* (action) {
    const { apiSchema, pageState, restArgs } = action;
    const { retry = 5 } = apiSchema;

    for (let i = 0; i < (retry); i += 1) {
        const journeyPayload = yield select(getJourneyPayload);

        if (journeyPayload.transaction) {
            apiSchema.requestAction = 'POST_PAYLOAD';
            const resultApiAction = apiAction(apiSchema, pageState, restArgs);
            yield put(resultApiAction);
            break;
        } else if (i < (retry - 1)) {
            yield call(retryTransactionFetching, pageState);
        } else {
            throw new Error('Journey payload requires transaction');
        }
    }
};

export const getTransactionApiSchema = () => {
    const page = pageSchema.pages.find((p) => p.name === 'start');
    return page && page.api && page.api.onMount;
};

export const retryTransactionFetching = function* (pageState) {
    const schema = getTransactionApiSchema();
    if (!schema) {
        yield call(delay, 3000);
        return;
    }
    // restArgs on the raw schema only carry {varPath, identifier} pairs - they must be
    // resolved against the current store state (via mapStateToArgs) into {varPath, value}
    // pairs before being handed to apiAction/buildUrl. Skipping this step (as before) meant
    // every retry built a URL with no resolved segments, e.g. POST /journey instead of
    // POST /journey/new/{brandCode}, which the backend has no route for and always 404s -
    // silently breaking this retry's ability to ever recover the journey transaction.
    const restArgs = yield select(mapStateToArgs, schema.restArgs);
    const transactionAction = apiAction(schema, pageState, restArgs, schema.baseUrl);
    yield put(transactionAction);

    // GET_NEW_JOURNEY_PAYLOAD is handled with takeLatest in root_saga.js, so dispatching it
    // again (on the loop's next iteration) before the in-flight request settles would cancel
    // the request that is currently running, and this retry would never live long enough to
    // see the journey transaction land in the store. Wait for the request to actually settle -
    // either its own "<type>_SUCCESS" action, or the fatal SET_CURRENT_ERROR dispatched by
    // logPayloadError() on failure - before letting the loop continue. A bounded delay is kept
    // only as a safety net in case neither signal ever arrives.
    const result = yield race({
        success: take(`${transactionAction.type}_SUCCESS`),
        failure: take('SET_CURRENT_ERROR'),
        timeout: call(delay, 8000)
    });

    if (result.success) {
        const journeyPayload = yield select(getJourneyPayload);
        if (!journeyPayload.transaction) {
            yield take('SET_JOURNEY_PAYLOAD');
        }
    }
};

export const getJourneyPayload = (state) => {
    const transaction = transactionPayloadSelector(state);
    return { transaction };
};

export default ['POST_PAYLOAD_IF_TRANSACTION', postPayloadIfTransaction];
